import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { MutatorResultErrorDetails } from "@rocicorp/zero";
import { useStore } from "@tanstack/react-form";
import { MealPlan } from "@corpuscomplete/core/nutrition/meal-plan";
import { DateRange } from "@corpuscomplete/core/common/date-time";
import { createId } from "@corpuscomplete/core/utils";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { CurrentActor } from "../../../actor";
import { AppForm } from "../../../form";

export namespace MealPlanForm {
  type UseCreateFormInput = {
    onSubmitting?: (props: { mealPlanId: string }) => void | Promise<void>;
    onCreated?: (props: { mealPlanId: string }) => void | Promise<void>;
    onCreateError?: (error: MutatorResultErrorDetails["error"]) => void | Promise<void>;
  };

  const CreateContext = createContext<ReturnType<typeof useCreateForm> | null>(null);
  export function CreateProvider({
    children,
    onSubmitting,
    onCreated,
    onCreateError,
  }: PropsWithChildren<UseCreateFormInput>) {
    const value = useCreateForm({ onSubmitting, onCreated, onCreateError });
    return <CreateContext.Provider value={value}>{children}</CreateContext.Provider>;
  }

  export function useCreate() {
    const form = useContext(CreateContext);
    if (!form) {
      throw new Error("useCreate must be used within CreateProvider");
    }
    return form;
  }

  export function useIsOverlapping() {
    const actor = CurrentActor.use();
    const form = useCreate();
    const dateRange = useStore(form.store, (state) => state.values.dateRange);
    const [existingPlans] = useQuery(queries.mealPlan.forUser({ userId: actor.userId }));

    return useMemo(() => {
      const overlappingPlans = existingPlans.filter((mealPlan) =>
        DateRange.overlaps(dateRange, {
          dateStart: mealPlan.dateStart,
          dateEnd: mealPlan.dateEnd,
        }),
      );

      return overlappingPlans.length > 0;
    }, [existingPlans, dateRange]);
  }

  export function useCreateForm({
    onSubmitting,
    onCreated,
    onCreateError,
  }: UseCreateFormInput = {}) {
    const { userId } = CurrentActor.use();
    const z = useZero();

    return AppForm.useAppForm({
      formId: `meal-plan-create.${userId}`,
      defaultValues: MealPlan.formDefault(Temporal.Now.plainDateISO()),
      validators: {
        onSubmit: MealPlan.createFormSchema,
      },
      onSubmit: async ({ value }) => {
        const mealPlanId = createId();
        const result = z.mutate(
          mutators.mealPlan.create(
            MealPlan.formToCreate({
              id: mealPlanId,
              ownerId: userId,
              form: value,
            }),
          ),
        );
        await onSubmitting?.({ mealPlanId });
        const serverResult = await result.server;
        if (serverResult.type === "success") {
          await onCreated?.({ mealPlanId });
        } else {
          await onCreateError?.(serverResult.error);
        }
      },
    });
  }
}
